// TanStack DB collections fed by PowerSync instead of Electric, so Owner and Admin keep their
// useLiveQuery code. Reads: a differential watch on the local SQLite table. Writes: the same
// sync-api REST calls as before (so forms still get the server's errors), then wait until
// PowerSync brings the change back, so the optimistic row hands over without a flicker.
import { createCollection, type Collection, type NonSingleResult } from "@tanstack/db";
import type { AbstractPowerSyncDatabase } from "@powersync/web";
import type { z } from "zod";
import { syncApiUrl } from "../sync-api-url.js";
import { fromRow } from "../powersync-schema.js";
import { TABLES, type TableName } from "../powersync-tables.js";
import { requestHeaders } from "./factory.js";
import { SAVE_REFUSED_EVENT } from "../powersync.js";

let bindDb!: (db: AbstractPowerSyncDatabase) => void;
const dbReady = new Promise<AbstractPowerSyncDatabase>((resolve) => (bindDb = resolve));

/** Hands the app's PowerSync database to every collection. Call once, before rendering. */
export function bindCollections(db: AbstractPowerSyncDatabase): void {
  bindDb(db);
}

const WAIT_MS = 20_000;
const REQUEST_TIMEOUT_MS = 30_000;

async function send(method: string, path: string, body?: unknown): Promise<Record<string, unknown> | undefined> {
  // Nothing queues these writes, so any failure rolls the row back: say so, as uploadData does.
  const refused = () => window.dispatchEvent(new Event(SAVE_REFUSED_EVENT));
  const res = await fetch(syncApiUrl(path), {
    method,
    headers: requestHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  }).catch((err) => {
    refused();
    throw err;
  });
  if (!res.ok) {
    if (!(method === "DELETE" && res.status === 404)) refused();
    throw new Error(`sync-api ${path} responded ${res.status}: ${await res.text().catch(() => "")}`);
  }
  return ((await res.json().catch(() => ({}))) as { row?: Record<string, unknown> }).row;
}

const time = (v: unknown) => (typeof v === "string" ? Date.parse(v.replace(" ", "T")) : NaN);

/** Resolves once the local copy of `id` passes `done`, or after WAIT_MS (the write itself already landed). */
async function waitForSync(table: string, id: string, done: (row: Record<string, unknown> | null) => boolean): Promise<void> {
  const db = await dbReady;
  const until = Date.now() + WAIT_MS;
  // ponytail: polls every 250ms; a per-table onChange listener if many writes queue at once.
  while (Date.now() < until) {
    if (done(await db.getOptional<Record<string, unknown>>(`SELECT * FROM ${table} WHERE id = ?`, [id]))) return;
    await new Promise((r) => setTimeout(r, 250));
  }
}

export function psCollection<S extends z.AnyZodObject>(
  name: TableName,
  schema: S,
  options: { select?: string; map?: (row: Record<string, unknown>) => Record<string, unknown> } = {},
): Collection<z.infer<S>, string, {}, S, z.input<S>> & NonSingleResult {
  type Row = z.infer<S> & { id: string };
  const route = TABLES[name][1];
  const parse = (r: Record<string, unknown>) => fromRow(schema, options.map ? options.map(r) : r) as Row;

  // Built untyped and cast: TypeScript can't pick createCollection's schema overload for a generic S.
  const config = {
    id: name,
    schema,
    getKey: (row: Row) => row.id,
    sync: {
      rowUpdateMode: "full",
      sync: ({
        begin,
        write,
        commit,
        markReady,
      }: {
        begin: () => void;
        write: (m: { type: "insert" | "update" | "delete"; value: Row }) => void;
        commit: () => void;
        markReady: () => void;
      }) => {
        let stopped = false;
        let close = () => {};
        void dbReady.then(async (db) => {
          if (stopped) return;
          const query = db.query<Record<string, unknown>>({ sql: options.select ?? `SELECT * FROM ${name}` }).differentialWatch();
          let firstResult!: () => void;
          const loaded = new Promise<void>((resolve) => (firstResult = resolve));
          const unlisten = query.registerListener({
            // The first result, even an empty one (which fires no diff), ends loading.
            onStateChange: (state) => {
              if (!state.isLoading) firstResult();
            },
            onDiff: (diff) => {
              if (!diff.added.length && !diff.removed.length && !diff.updated.length) return;
              begin();
              for (const r of diff.removed) write({ type: "delete", value: parse(r) });
              for (const r of diff.added) write({ type: "insert", value: parse(r) });
              for (const u of diff.updated) write({ type: "update", value: parse(u.current) });
              commit();
            },
          });
          close = () => {
            unlisten();
            void query.close();
          };
          // Ready means "has real data": the first full sync landed and its rows are written here.
          await Promise.all([db.waitForFirstSync(), loaded]);
          if (!stopped) markReady();
        });
        return () => {
          stopped = true;
          close();
        };
      },
    },
    onInsert: async ({ transaction }: { transaction: { mutations: Array<{ modified: unknown }> } }) => {
      for (const m of transaction.mutations) {
        const row = m.modified as Row;
        await send("POST", route, row);
        await waitForSync(name, row.id, (local) => local != null);
      }
    },
    onUpdate: async ({ transaction }: { transaction: { mutations: Array<{ original: unknown; changes: unknown }> } }) => {
      for (const m of transaction.mutations) {
        const id = (m.original as Row).id;
        const saved = await send("PATCH", `${route}/${id}`, m.changes);
        const at = time(saved?.updated_at);
        await waitForSync(name, id, (local) => local != null && (Number.isNaN(at) || time(local.updated_at) >= at));
      }
    },
    onDelete: async ({ transaction }: { transaction: { mutations: Array<{ original: unknown }> } }) => {
      for (const m of transaction.mutations) {
        const id = (m.original as Row).id;
        await send("DELETE", `${route}/${id}`);
        // Some routes soft-delete (deleted_at); either way the row is gone for the UI.
        await waitForSync(name, id, (local) => local == null || local.deleted_at != null);
      }
    },
  };
  return createCollection(config as never) as never;
}
